import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Container } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useState } from "react";
import { useNavigate } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { convertISODate, convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import adminApi from "../../../../api/adminApi";
import type { UserConsentInfo } from "../../../../api/types/userConsentTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import CreateUserConsentDialog from "./CreateUserConsentDialog";
import DeleteUserConsentDialog from "./DeleteUserConsentDialog";
import { ActionCell, CategoryCell } from "./userConsentsGridDataProvider";
import { UserConsentsSectionContextProvider } from "./UserConsentsSectionContextProvider";

const getConsentPath = (clientCode: string, consentName: string) =>
  `/${clientCode}/${pageRoutes.investorRelations}/${pageRoutes.userConsents}/${consentName}`;

const NameCell = ({ row }: { row: UserConsentInfo }) => {
  const { clientCode } = useClientContext();

  return <GridTextHoverLink to={getConsentPath(clientCode, row.name)} text={row.name} />;
};

const columnDefinitions: GridColDef<UserConsentInfo>[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 2,
    renderCell: ({ row }) => <NameCell row={row} />,
  },
  {
    field: "categoryId",
    headerName: "Category",
    flex: 1,
    minWidth: 250,
    renderCell: ({ row }) => <CategoryCell row={row} />,
  },
  {
    field: "activeFrom",
    headerName: "Activation date",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value) => (value ? convertISODate(value) : "-"),
  },
  {
    field: "lastModified",
    headerName: "Modified at",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value) => (value ? convertISODateTime(value) : "-"),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    cellClassName: "grid-row-actions",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => <ActionCell row={row} />,
  },
];

interface DialogState {
  openDialog?: "create" | "delete";
  consentNameToDelete?: string;
}

const getRowId = (row: UserConsentInfo) => row.name;

const UserConsentsSection = () => {
  const { clientCode, hasPermissions, accessCategories } = useClientContext();
  const navigate = useNavigate();

  const [consents, fetchConsentsError, { setData: setConsents }] = useFetch(adminApi.getUserConsents);

  const [dialogState, setDialogState] = useState<DialogState>({});

  if (fetchConsentsError) {
    logError(fetchConsentsError, "[UserConsentsSection] fetchError");
    return <DataLoadingFailed />;
  }

  const hasEditPermissions = hasPermissions(["ManageInvestorPortalSettings"]);

  const loading = !consents;

  const navigateToConsent = (name: string) => {
    navigate(getConsentPath(clientCode, name));
  };

  const handleCreateConsent = () => {
    setDialogState({ openDialog: "create" });
  };

  const handleConsentCreated = (consentName: string) => {
    setDialogState({});
    navigateToConsent(consentName);
  };

  const handleDeleteConsent = (consentNameToDelete: string) => {
    setDialogState({ openDialog: "delete", consentNameToDelete });
  };

  const handleConsentDeleted = (consentName: string) => {
    setDialogState({});
    setConsents((prev) => prev?.filter((consent) => consent.name !== consentName));
  };

  return (
    <UserConsentsSectionContextProvider
      categories={accessCategories}
      hasEditPermissions={hasEditPermissions}
      onDeleteConsent={handleDeleteConsent}
    >
      <Container maxWidth="xl" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Box sx={{ py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <RecordCounter records={consents?.length ?? 0} hide={loading} />
          {hasEditPermissions && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateConsent}>
              Add New
            </Button>
          )}
        </Box>

        <DataGrid<UserConsentInfo>
          columns={columnDefinitions}
          rows={consents ?? []}
          getRowId={getRowId}
          loading={loading}
          noRowsText="No user consents yet"
          disableColumnPinning
          disableColumnReorder
          disableColumnSorting
        />
      </Container>
      {dialogState.openDialog === "create" && (
        <CreateUserConsentDialog
          onClose={() => setDialogState({})}
          onCreated={handleConsentCreated}
          existingConsentNames={consents?.map((c) => c.name) ?? []}
        />
      )}
      {dialogState.openDialog === "delete" && dialogState.consentNameToDelete && (
        <DeleteUserConsentDialog
          open
          consentName={dialogState.consentNameToDelete}
          onClose={() => setDialogState({})}
          onDeleted={handleConsentDeleted}
        />
      )}
    </UserConsentsSectionContextProvider>
  );
};

export default UserConsentsSection;
